export interface InlineAudioChunk {
  data: string;
  mimeType?: string;
}

export type PlaybackState = "idle" | "playing" | "error";

export interface AudioPlaybackDebugState {
  enqueueCount: number;
  decodeCount: number;
  startCount: number;
  endedCount: number;
  errorCount: number;
  lastEnqueuedAt: number | null;
  lastDecodedAt: number | null;
  lastStartedAt: number | null;
  lastEndedAt: number | null;
  lastErrorAt: number | null;
  lastEnqueuedDataLength: number;
  lastDecodedFrameCount: number;
  lastDecodedDurationMs: number;
  activeSourceCount: number;
  audioContextState: AudioContextState;
}

interface AudioPlaybackQueueOptions {
  additionalDestinations?: AudioNode[];
  audioContext?: AudioContext;
  onDebugStateChange?: (state: AudioPlaybackDebugState) => void;
  onStateChange?: (state: PlaybackState) => void;
}

function base64ToUint8Array(value: string): Uint8Array {
  const binary = window.atob(value);
  const bytes = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return bytes;
}

function parseSampleRate(mimeType: string | undefined): number {
  if (!mimeType) {
    return 24000;
  }

  const match = mimeType.match(/rate=(\d+)/i);
  if (!match) {
    return 24000;
  }

  const rate = match[1];
  if (!rate) {
    return 24000;
  }

  const parsed = Number.parseInt(rate, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 24000;
}

function decodePcm16(bytes: Uint8Array, sampleRate: number, context: AudioContext): AudioBuffer {
  const frameCount = Math.floor(bytes.byteLength / 2);
  const buffer = context.createBuffer(1, frameCount, sampleRate);
  const channel = buffer.getChannelData(0);
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);

  for (let index = 0; index < frameCount; index += 1) {
    channel[index] = view.getInt16(index * 2, true) / 32768;
  }

  return buffer;
}

async function decodeAudioChunk(
  chunk: InlineAudioChunk,
  context: AudioContext
): Promise<AudioBuffer> {
  const bytes = base64ToUint8Array(chunk.data);
  const mimeType = chunk.mimeType ?? "";

  if (mimeType.startsWith("audio/pcm")) {
    return decodePcm16(bytes, parseSampleRate(mimeType), context);
  }

  const arrayBuffer = bytes.buffer.slice(
    bytes.byteOffset,
    bytes.byteOffset + bytes.byteLength
  ) as ArrayBuffer;
  return context.decodeAudioData(arrayBuffer);
}

export class AudioPlaybackQueue {
  private readonly additionalDestinations: AudioNode[];
  private readonly audioContext: AudioContext;
  private readonly ownContext: boolean;
  private readonly onDebugStateChange: ((state: AudioPlaybackDebugState) => void) | undefined;
  private readonly onStateChange: ((state: PlaybackState) => void) | undefined;
  private readonly activeSources = new Set<AudioBufferSourceNode>();
  private debugState: AudioPlaybackDebugState;
  private nextStartTime = 0;
  private state: PlaybackState = "idle";

  constructor(options: AudioPlaybackQueueOptions = {}) {
    this.audioContext = options.audioContext ?? new AudioContext();
    this.additionalDestinations = options.additionalDestinations ?? [];
    this.ownContext = !options.audioContext;
    this.onDebugStateChange = options.onDebugStateChange;
    this.onStateChange = options.onStateChange;
    this.debugState = {
      enqueueCount: 0,
      decodeCount: 0,
      startCount: 0,
      endedCount: 0,
      errorCount: 0,
      lastEnqueuedAt: null,
      lastDecodedAt: null,
      lastStartedAt: null,
      lastEndedAt: null,
      lastErrorAt: null,
      lastEnqueuedDataLength: 0,
      lastDecodedFrameCount: 0,
      lastDecodedDurationMs: 0,
      activeSourceCount: 0,
      audioContextState: this.audioContext.state,
    };
    this.emitDebugState();
  }

  async resume() {
    if (this.audioContext.state === "suspended") {
      await this.audioContext.resume();
    }
  }

  async enqueue(chunk: InlineAudioChunk) {
    if (chunk.data.length === 0) {
      return;
    }

    try {
      this.setDebugState({
        enqueueCount: this.debugState.enqueueCount + 1,
        lastEnqueuedAt: Date.now(),
        lastEnqueuedDataLength: chunk.data.length,
      });
      await this.resume();
      const decoded = await decodeAudioChunk(chunk, this.audioContext);
      this.setDebugState({
        decodeCount: this.debugState.decodeCount + 1,
        lastDecodedAt: Date.now(),
        lastDecodedFrameCount: decoded.length,
        lastDecodedDurationMs: Math.round(decoded.duration * 1000),
      });
      const source = this.audioContext.createBufferSource();
      source.buffer = decoded;
      source.connect(this.audioContext.destination);
      for (const destination of this.additionalDestinations) {
        source.connect(destination);
      }

      const startAt = Math.max(this.audioContext.currentTime + 0.01, this.nextStartTime);
      this.nextStartTime = startAt + decoded.duration;
      this.activeSources.add(source);
      this.setState("playing");

      source.addEventListener("ended", () => {
        this.activeSources.delete(source);
        this.setDebugState({
          endedCount: this.debugState.endedCount + 1,
          lastEndedAt: Date.now(),
          activeSourceCount: this.activeSources.size,
        });
        if (
          this.activeSources.size === 0 &&
          this.audioContext.currentTime >= this.nextStartTime - 0.05
        ) {
          this.setState("idle");
        }
      });

      source.start(startAt);
      this.setDebugState({
        startCount: this.debugState.startCount + 1,
        lastStartedAt: Date.now(),
        activeSourceCount: this.activeSources.size,
      });
    } catch {
      this.clear();
      this.setDebugState({
        errorCount: this.debugState.errorCount + 1,
        lastErrorAt: Date.now(),
      });
      this.setState("error");
    }
  }

  clear() {
    for (const source of this.activeSources) {
      try {
        source.stop();
      } catch {}
    }

    this.activeSources.clear();
    this.nextStartTime = this.audioContext.currentTime;
    this.setDebugState({ activeSourceCount: 0 });
    this.setState("idle");
  }

  async close() {
    this.clear();

    if (this.ownContext) {
      await this.audioContext.close();
    }
  }

  private setState(nextState: PlaybackState) {
    if (this.state === nextState) {
      return;
    }

    this.state = nextState;
    this.onStateChange?.(nextState);
  }

  private setDebugState(nextState: Partial<AudioPlaybackDebugState>) {
    this.debugState = {
      ...this.debugState,
      ...nextState,
      activeSourceCount: nextState.activeSourceCount ?? this.activeSources.size,
      audioContextState: this.audioContext.state,
    };
    this.emitDebugState();
  }

  private emitDebugState() {
    this.onDebugStateChange?.(this.debugState);
  }
}
